import { ref } from 'vue'

export interface VideoComponent {
    pause: () => void
    play: () => void
}

class GlobalVideoManager {
    // 当前播放的视频ID
    private currentPlayingId = ref<string | null>(null)

    // 注册的视频组件
    private registeredVideos = new Map<string, VideoComponent>()

    /**
     * 注册视频组件
     * @param id 视频唯一标识
     * @param videoComponent 视频组件实例，需要包含 pause 和 play 方法
     */
    registerVideo(id: string, videoComponent: VideoComponent) {
        this.registeredVideos.set(id, videoComponent)
    }

    /**
     * 注销视频组件
     * @param id 视频唯一标识
     */
    unregisterVideo(id: string) {
        this.registeredVideos.delete(id)
        // 如果注销的是当前播放的视频，清空当前播放ID
        if (this.currentPlayingId.value === id) {
            this.currentPlayingId.value = null
        }
    }

    /**
     * 播放指定视频，暂停其他所有视频
     * @param id 视频唯一标识
     */
    playVideo(id: string) {
        // 如果当前有视频在播放，先暂停它
        if (this.currentPlayingId.value && this.currentPlayingId.value !== id) {
            const currentVideo = this.registeredVideos.get(this.currentPlayingId.value)
            currentVideo?.pause()
        }

        // 播放新视频
        const targetVideo = this.registeredVideos.get(id)
        if (targetVideo) {
            targetVideo.play()
            this.currentPlayingId.value = id
        }
    }

    /**
     * 暂停指定视频
     * @param id 视频唯一标识
     */
    pauseVideo(id: string) {
        const video = this.registeredVideos.get(id)
        video?.pause()
        if (this.currentPlayingId.value === id) {
            this.currentPlayingId.value = null
        }
    }

    /**
     * 暂停所有视频
     */
    pauseAllVideos() {
        this.registeredVideos.forEach((video) => {
            video.pause()
        })
        this.currentPlayingId.value = null
    }

    /**
     * 获取当前播放的视频ID
     */
    getCurrentPlayingId() {
        return this.currentPlayingId.value
    }

    /**
     * 检查指定视频是否正在播放
     * @param id 视频唯一标识
     */
    isPlaying(id: string) {
        return this.currentPlayingId.value === id
    }

    /**
     * 获取已注册的视频数量
     */
    getRegisteredVideoCount() {
        return this.registeredVideos.size
    }

    /**
     * 清空所有注册的视频
     */
    clearAllVideos() {
        this.pauseAllVideos()
        this.registeredVideos.clear()
    }

    /**
     * 获取所有已注册的视频ID列表
     */
    getRegisteredVideoIds() {
        return Array.from(this.registeredVideos.keys())
    }

    /**
     * 检查指定ID的视频是否已注册
     * @param id 视频唯一标识
     */
    isRegistered(id: string) {
        return this.registeredVideos.has(id)
    }
}

// 创建全局单例实例
const globalVideoManagerInstance = new GlobalVideoManager()

/**
 * 全局视频播放管理器 Hook
 * 用于管理应用中所有视频的播放状态，确保同一时间只有一个视频在播放
 * 
 * @example
 * ```ts
 * // 在组件中使用
 * const videoManager = useGlobalVideoManager()
 * 
 * // 注册视频
 * videoManager.registerVideo('video-1', { pause, play })
 * 
 * // 播放视频
 * videoManager.playVideo('video-1')
 * 
 * // 暂停所有视频
 * videoManager.pauseAllVideos()
 * 
 * // 注销视频
 * onUnmounted(() => {
 *   videoManager.unregisterVideo('video-1')
 * })
 * ```
 */
export function useGlobalVideoManager() {
    return globalVideoManagerInstance
}

